<template>
    <div>
        <Upload
            :ref="e => (refs.upload = e)"
            :multiple="data.multiple.replaceItem ? undefined : multiple"
            :accept="accept"
            :showUploadList="showUploadList"
            :listType="listType"
            :fileList="fileListCardPictureComputed"
            :beforeUpload="() => false"
            @change="onChange"
            @preview="file => onPreview({ file, data })"
            v-if="!disabled || showUploadList"
            :disabled="disabled"
        >
            <Button size="small" v-if="!disabled">
                <CIcon file="upload.svg"></CIcon>
                <span>{{ $t("button.upload") }}</span>
            </Button>
        </Upload>
        <template v-if="fileListShowMode === 'vertical'">
            <template v-for="file in fileListComputed" :key="JSON.stringify(file)">
                <div>
                    <CIcon
                        file="delete.svg"
                        v-if="!disabled && showDelete?.({ file })"
                        class="text-danger mr-2"
                        @click="() => onClickDelete(file)"
                    ></CIcon>
                    <CIcon
                        file="edit.svg"
                        v-if="!disabled && multiple && showEdit?.({ file })"
                        class="text-warning mr-2"
                        @click="() => onClickEdit(file)"
                    ></CIcon>
                    <a href="javascript:void(0)" @click="() => download({ file })">{{ file.name }}</a>
                </div>
            </template>
        </template>
        <template v-if="fileListShowMode === 'horizontal'">
            <template v-for="(file, index) in fileListComputed" :key="JSON.stringify(file)">
                <div class="d-inline-block ml-2">
                    <CIcon
                        file="delete.svg"
                        v-if="!disabled && showDelete?.({ file })"
                        class="text-danger mr-2"
                        @click="() => onClickDelete(file)"
                    ></CIcon>
                    <CIcon
                        file="edit.svg"
                        v-if="!disabled && multiple && showEdit?.({ file })"
                        class="text-warning mr-2"
                        @click="() => onClickEdit(file)"
                    ></CIcon>
                    <a href="javascript:void(0)" @click="() => download({ file })">{{ file.name }}</a>
                    <span class="ml-2" v-if="index + 1 < fileListComputed.length">|</span>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import { computed, defineAsyncComponent, defineComponent, onMounted, reactive, toRefs } from "vue";
import { Button, Upload } from "ant-design-vue";
import { download, getBase64 } from "../../../../utils/file";
import { parseUrl } from "../../../../utils/util";
import { onPreview } from "./attachment.logic";
import lodash from "lodash";

export default defineComponent({
    components: {
        Upload,
        Button,
        CIcon: defineAsyncComponent(() => import("../../../icon/CIcon"))
    },
    props: {
        value: {},
        group: {},
        isUserFile: {},
        attribute: {},
        disabled: {},
        accept: {},
        listType: {},
        multiple: { default: false },
        showUploadList: { default: false },
        fileListShowMode: { default: "vertical" },
        showDelete: { default: () => () => true },
        showEdit: { default: () => () => true }
    },
    emits: ["update:value", "replace:value"],
    setup(props, { emit }) {
        const { value } = toRefs(props);
        const data = reactive({ multiple: { replaceItem: null }, defaultOnClickUpload: null });
        const refs = reactive({ upload: null });
        const onChange = e => {
            getBase64(e.file, fileModel => {
                const file = { group: props.group, isUserFile: props.isUserFile, attribute: props.attribute, ...fileModel, isSave: true };
                if (data.multiple.replaceItem) {
                    const replaceItemClone = lodash.cloneDeep(data.multiple.replaceItem);
                    lodash.merge(data.multiple.replaceItem, file);
                    emit("replace:value", { oldFile: replaceItemClone, newFile: data.multiple.replaceItem });
                    emit("update:value", props.value);
                    return;
                }
                if (!props.multiple) {
                    emit("update:value", file);
                } else {
                    value.value.push(file);
                    emit("update:value", props.value);
                }
            });
        };
        const onClickDelete = file => {
            if (!props.multiple) {
                emit("update:value", null);
            } else {
                if (file.id) {
                    file.isDelete = true;
                } else {
                    lodash.remove(props.value, value => value === file);
                }
                emit("update:value", props.value);
            }
        };
        const onClickEdit = file => {
            Promise.resolve()
                .then(() => (data.multiple.replaceItem = file))
                .then(() => data.defaultOnClickUpload());
        };
        const fileListComputed = computed(() => {
            if (!props.multiple) {
                if (props.value) {
                    return [props.value];
                }
            }
            return (props.value || []).filter(d => d.isDelete !== true);
        });
        const fileListCardPictureComputed = computed(() => {
            if (!props.listType || !props.showUploadList) {
                return undefined;
            }
            if (!props.multiple) {
                if (props.value) {
                    return [props.value];
                }
            }
            const fileListComputed = lodash.cloneDeep((props.value || []).filter(d => d.isDelete !== true));
            return fileListComputed.map(file => ({
                ...file,
                uid: file.id,
                name: file.name,
                url: !file?.type?.includes?.("image/")
                    ? undefined
                    : file?.blob && file?.type && file?.name
                    ? `data:${file.type};base64,${file.blob}`
                    : parseUrl(file.url),
                status: "done"
            }));
        });
        onMounted(() => {
            const interval = setInterval(() => {
                data.defaultOnClickUpload = refs.upload?.$refs?.uploadRef?.$refs?.uploaderRef?.onClick;
                if (data.defaultOnClickUpload) {
                    refs.upload.$refs.uploadRef.$refs.uploaderRef.onClick = () => {
                        Promise.resolve()
                            .then(() => (data.multiple.replaceItem = null))
                            .then(() => data.defaultOnClickUpload());
                    };
                    clearInterval(interval);
                }
            }, 200);
        });
        return { data, refs, onChange, onPreview, parseUrl, fileListComputed, fileListCardPictureComputed, onClickDelete, onClickEdit, download };
    }
});
</script>

<style scoped>
.upload-body {
    position: relative;
}

.upload-body .upload-action {
    display: none;
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    height: 100%;
}

.upload-body:hover .upload-action {
    display: block;
    transition: all 0.3s;
    background: rgba(0, 0, 0, 0.5);
}
</style>

<style>
.w-100 .ant-upload {
    width: 100% !important;
}
</style>
